export type UseCaseSection = {
  heading: string;
  body: string;
};

export type UseCaseFaq = {
  q: string;
  a: string;
};

export type UseCaseContent = {
  path: string;
  h1: string;
  lead: string;
  sections: UseCaseSection[];
  example: { heading: string; items: string[] };
  faqs: UseCaseFaq[];
};

export const USE_CASE_PAGES: UseCaseContent[] = [
  {
    path: "/packing-list",
    h1: "A packing list you reuse and share",
    lead:
      "NeatTick is a free web packing list you build once, reuse for the next trip, and share so everyone checks items off on the same list. Live checkmarks when two or more people are packing.",
    sections: [
      {
        heading: "Why not a notes app",
        body:
          "A note is a blob of text. A packing list you reuse needs a fresh copy each trip, checkmarks that do not rewrite last year's list, and a way to share so two people are not both packing the sunscreen. Notes and Keep were not built for that.",
      },
      {
        heading: "How a reusable packing list works",
        body:
          "Build a list once (carry-on, toiletries, kids' bag). Snap lists into a template for the kind of trip you take. Instantiate the template into a checklist for this weekend. Editing the source list never changes checklists you have already started.",
      },
      {
        heading: "Share with family",
        body:
          "Send a link. View-only if you just want them to see the list; can-check if they are packing too. When two or more people are on it, checkmarks sync live and you can see who checked what.",
      },
      {
        heading: "Start from a published list",
        body:
          "Copy Beach Day, Carry-On, or Camping lists from the community into your workspace, then edit them for your family. Packing is one job NeatTick is good at — not the only one.",
      },
    ],
    example: {
      heading: "A weekend bag, as a starting point",
      items: [
        "Passport / ID",
        "Phone charger and cable",
        "Medications",
        "Sunscreen",
        "Swimsuit",
        "One outfit per day plus a spare",
      ],
    },
    faqs: [
      {
        q: "Can more than one person pack from the same list?",
        a: "Yes. Share the checklist link. Live checkmarks appear when two or more people have it open.",
      },
      {
        q: "Does editing my packing list change last trip's checklist?",
        a: "No. Each checklist is a snapshot. Change the list or template for next time; in-progress trips stay as they were.",
      },
      {
        q: "Is NeatTick only for packing?",
        a: "No. Packing is a common job. The same list → template → checklist model works for events, onboarding, and any process you repeat.",
      },
    ],
  },
  {
    path: "/shared-checklist",
    h1: "A shared checklist with live checkmarks",
    lead:
      "Share a NeatTick checklist with a link. When two or more people open it, checkmarks appear live and you can see who checked what.",
    sections: [
      {
        heading: "One list, not a group text",
        body:
          "Forwarding a screenshot goes stale in ten minutes. A shared checklist is the current truth: what is done, who did it, and what is left.",
      },
      {
        heading: "View-only or can-check",
        body:
          "Give someone view-only access if they only need to read the run sheet. Let others check items off. Sign-in is required so the UI can show who checked what.",
      },
      {
        heading: "Live when it matters",
        body:
          "When two or more people are on the same checklist, checkmarks sync live. One person packing the car, another at the house — both see the same ticks.",
      },
    ],
    example: {
      heading: "A shared grocery run",
      items: [
        "Milk",
        "Eggs",
        "Coffee",
        "Trash bags",
        "The thing someone texted you in the store",
      ],
    },
    faqs: [
      {
        q: "Do people I share with need an account?",
        a: "Yes, so we can show who checked what — signup takes seconds.",
      },
      {
        q: "When do checkmarks sync live?",
        a: "When two or more people are members of the checklist. Until then it is just your list.",
      },
      {
        q: "Can I share without letting someone edit the items?",
        a: "Yes. View-only can see the list and who checked what; can-check can tick items. Item text still belongs to the owner.",
      },
    ],
  },
  {
    path: "/wedding-checklist",
    h1: "A wedding day-of checklist you can share with the crew",
    lead:
      "One run sheet for the day. View-only for vendors, can-check for the people running the timeline.",
    sections: [
      {
        heading: "Keep the couple off group chat",
        body:
          "The day-of list is for the people running the timeline: planner, siblings, photographer's second. Share it instead of asking the couple whether the rings made it.",
      },
      {
        heading: "View-only for vendors",
        body:
          "Florists and caterers can see the run sheet without ticking items. The people who own the timeline get can-check.",
      },
      {
        heading: "Reuse it for the next event",
        body:
          "Save the day-of list as a template. The next wedding, dinner, or ceremony starts from that blueprint instead of a blank note.",
      },
    ],
    example: {
      heading: "A short day-of run sheet",
      items: [
        "Rings at the venue",
        "Vows printed / on phones",
        "Vendor check-in times confirmed",
        "Emergency kit (stain stick, safety pins, snacks)",
        "Timeline printed for the officiant",
        "Someone assigned to the guestbook",
      ],
    },
    faqs: [
      {
        q: "Can vendors see the list without checking things off?",
        a: "Yes. Share view-only. Can-check is for the people actually running the timeline.",
      },
      {
        q: "Will editing the template change a wedding already in progress?",
        a: "No. Instantiated checklists are snapshots.",
      },
      {
        q: "Is this a wedding-planning app?",
        a: "No. It is a shareable run sheet you can reuse. Guest lists, seating, and registries live elsewhere.",
      },
    ],
  },
  {
    path: "/camping-checklist",
    h1: "A camping packing checklist for the whole group",
    lead:
      "Reuse last trip's list. Share it so the tent is not packed twice and the stove is packed once.",
    sections: [
      {
        heading: "Last trip, already written down",
        body:
          "The stove, the spare headlamp, the cable everyone forgets — they belong on a list you instantiate again, not a memory from the drive home.",
      },
      {
        heading: "Share the pack-out",
        body:
          "One person has the tent. Another has food. Live checkmarks mean you see what left whose garage without a thread of texts.",
      },
      {
        heading: "Group gear vs personal gear",
        body:
          "Keep group gear on one list (or a subgroup) and personal packing on another. Combine them in a template for this campsite.",
      },
    ],
    example: {
      heading: "Group gear worth not forgetting",
      items: [
        "Tent and stakes",
        "Sleeping pads",
        "Stove and fuel",
        "Headlamps",
        "First-aid kit",
        "The cable everyone forgets",
      ],
    },
    faqs: [
      {
        q: "Can each person pack from the same list?",
        a: "Yes. Share can-check. You will see who packed the stove.",
      },
      {
        q: "Do I have to start from scratch every season?",
        a: "No. Reuse last trip's list or copy a camping list from the community, then instantiate a fresh checklist.",
      },
      {
        q: "What if someone adds an item mid-pack?",
        a: "Owners can edit the source list for next time. The checklist you already started is a snapshot — add the extra item on that checklist if you still need it this trip.",
      },
    ],
  },
  {
    path: "/onboarding-checklist",
    h1: "A new-hire onboarding checklist you run every time",
    lead:
      "Instantiate a template per hire. Share the checklist so the new person sees what is done. Editing the template does not change in-progress hires (snapshot).",
    sections: [
      {
        heading: "One blueprint, many hires",
        body:
          "Laptop, accounts, buddy intro, first-week goals — they barely change. A template is the blueprint; each hire gets a fresh checklist.",
      },
      {
        heading: "The new person can see the list",
        body:
          "Share it so they know what is done and what is still on IT. View-only if they should not tick your internal tasks; can-check if they own some of them.",
      },
      {
        heading: "Change the process without rewriting last month",
        body:
          "When you add a security training to the template, in-progress onboarding checklists stay as they were. Next hire gets the new step. That is the snapshot rule.",
      },
    ],
    example: {
      heading: "A first-week skeleton",
      items: [
        "Laptop and accounts provisioned",
        "Payroll / HR forms",
        "Security training",
        "Buddy intro scheduled",
        "Tools walkthrough",
        "First-week goals written down",
      ],
    },
    faqs: [
      {
        q: "Can the new hire see what IT already finished?",
        a: "Yes. Share the checklist. Live checkmarks show what landed.",
      },
      {
        q: "If I update the onboarding template, do current hires change?",
        a: "No. Each checklist is a snapshot. Edit the template for the next person.",
      },
      {
        q: "Is this only for hiring?",
        a: "No. The same pattern works for client kickoff, a website launch, or any process you run more than once.",
      },
    ],
  },
];

export function useCaseByPath(path: string): UseCaseContent | undefined {
  return USE_CASE_PAGES.find((page) => page.path === path);
}
